import React from "react";
import Select from "react-select";
import PerformerDropdownIndicator from "./PerformerDropdownIndicator";
import { ESTABLISHMENT_TYPE } from "../../../../utils/constants";
import {
  EMPLOYEE_UPLOAD_MONTH_LIST,
  generateEmployeeUploadYearsList,
  EmployeeUploadFilterState,
  sortEmployeeUploadCompaniesByName,
} from "../../../../utils/employeeUploads.utils";


const ESTABLISHMENT_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  ...Object.values(ESTABLISHMENT_TYPE).map(
    (each) => ({
      value: each,
      label: each,
    }),
  ),
];

interface EmployeeUploadFiltersProps {
  filters: EmployeeUploadFilterState;
  companies: any[];
  associateCompanies: any[];
  locations: any[];
  onUpdateFilter: (field: keyof EmployeeUploadFilterState, value: any) => void;
  // Called synchronously, in the same handler as the company change, so the
  // associate-company data for the new company is requested in the SAME
  // render pass as the filter update — not from a separate useEffect a
  // render later. Without this, useEmployeeUploadCompanyAutoSelect's
  // associate-company auto-select effect can run against the PREVIOUS
  // company's still-cached associateCompanies for one render, auto-pick one
  // of ITS associate companies, and then never correct itself (its own
  // "already selected — don't clobber" guard blocks any later fix once the
  // real list for the new company arrives).
  fetchAssociateCompanies: (companyId: string) => void;
  showYearMonth?: boolean;
  showAdditionalFilters?: boolean;
  onAdditionalFiltersClick?: () => void;
  actions?: React.ReactNode;
}

const EmployeeUploadFilters: React.FC<EmployeeUploadFiltersProps> = ({
  filters,
  companies,
  associateCompanies,
  locations,
  onUpdateFilter,
  fetchAssociateCompanies,
  showYearMonth = false,
  showAdditionalFilters = false,
  onAdditionalFiltersClick,
  actions,
}) => {
  const yearsList = generateEmployeeUploadYearsList();

  const ALL_OPTION = { value: '', label: 'All' };

  // Sorted alphabetically — the auto-select in useEmployeeUploadCompanyAutoSelect
  // picks "first alphabetically", so the dropdown needs to agree on what "first" means.
  const companyOptions = [ALL_OPTION, ...[...companies]
    .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""))
    .map((c: any) => ({
      value: c.id,
      label: c.name,
    }))];
  const associateCompanyOptions = [ALL_OPTION, ...associateCompanies.map((c: any) => ({
    value: c.id,
    label: c.name,
  }))];

  const locationOptions = [ALL_OPTION, ...(locations || [])
    .filter((each: any) => {
      return each.location_id && each.state;
    })
    .slice()
    .sort((a: any, b: any) => {
      const stateCmp = ((a.state && a.state.name) || "").localeCompare((b.state && b.state.name) || "");
      if (stateCmp !== 0) return stateCmp;
      return (a.name || "").localeCompare(b.name || "");
    })
    .map((each: any) => {
      const { name, code, city, state } = each;
      return {
        value: each.location_id + "^" + state.id,
        label: `${name} (${state.code}-${(city || {}).code}-${code})`,
      };
    })];

  const yearOptions = [ALL_OPTION, ...yearsList.map((y: any) => ({
    value: y,
    label: String(y),
  }))];
  const monthOptions = [ALL_OPTION, ...EMPLOYEE_UPLOAD_MONTH_LIST.map((m: any) => ({
    value: m,
    label: m,
  }))];

  const selectedEstablishmentType = filters.establishmentType
    ? { value: filters.establishmentType, label: filters.establishmentType }
    : ALL_OPTION;
  const selectedCompany = filters.company
    ? companyOptions.find((o) => o.value === filters.company) || null
    : null;
  const selectedAssociateCompany = filters.associateCompany
    ? associateCompanyOptions.find(
        (o) => o.value === filters.associateCompany,
      ) || ALL_OPTION
    : ALL_OPTION;
  const selectedLocation = filters.location
    ? locationOptions.find((o) => o.value === filters.location) || ALL_OPTION
    : ALL_OPTION;
  const selectedYear = filters.year
    ? { value: filters.year, label: String(filters.year) }
    : ALL_OPTION;
  const selectedMonth = filters.month
    ? { value: filters.month, label: filters.month }
    : ALL_OPTION;

  const handleEstablishmentTypeChange = (option: any) => {
    onUpdateFilter("establishmentType", option ? option.value : "");
  };

  const handleCompanyChange = (option: any) => {
    const newValue = option ? option.value : "";
    // Request the new company's associate companies in this SAME handler —
    // see the fetchAssociateCompanies prop comment for why this can't wait
    // for a useEffect.
    if (newValue) fetchAssociateCompanies(newValue);
    onUpdateFilter("company", newValue);
  };

  const handleAssociateCompanyChange = (option: any) => {
    onUpdateFilter("associateCompany", option ? option.value : "");
  };

  const handleLocationChange = (option: any) => {
    onUpdateFilter("location", option ? option.value : "");
  };

  const handleYearChange = (option: any) => {
    onUpdateFilter("year", option ? option.value : "");
  };

  const handleMonthChange = (option: any) => {
    onUpdateFilter("month", option ? option.value : "");
  };

  return (
    <div>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        justifyContent: "space-between",
        width: "100%",
        marginBottom: "24px",
        gap: "12px",
      }}>
        <div className="filter-subcontainer" style={{ maxWidth: "250px" }}>
          <label className="filter-label">
            <small>Establishment Type</small>
          </label>
          <Select
            options={ESTABLISHMENT_TYPE_OPTIONS}
            value={selectedEstablishmentType}
            onChange={handleEstablishmentTypeChange}
            className="select-control"
            components={{ DropdownIndicator: PerformerDropdownIndicator }}
            isSearchable={false}
          />
        </div>

          {actions && (
            <div className="d-flex align-items-end px-2" style={{ gap: "10px" }}>
              {actions}
            </div>
          )}
      </div>

      <div style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        justifyContent: "space-between",
        width: "100%",
        gap: "12px",
      }}>
        <div className="filter-subcontainer">
          <label className="filter-label">
            <small>Company</small>
          </label>
          <Select
            options={companyOptions}
            value={selectedCompany}
            onChange={handleCompanyChange}
            className="select-control"
            components={{ DropdownIndicator: PerformerDropdownIndicator }}
          />
        </div>

        <div className="filter-subcontainer">
          <label className="filter-label">
            <small>Associate Company</small>
          </label>
          <Select
            options={associateCompanyOptions}
            value={selectedAssociateCompany}
            onChange={handleAssociateCompanyChange}
            isDisabled={!filters.establishmentType || !filters.company}
            className="select-control"
            components={{ DropdownIndicator: PerformerDropdownIndicator }}
          />
        </div>

        <div className="filter-subcontainer">
          <label className="filter-label">
            <small>Location</small>
          </label>
          <Select
            options={locationOptions}
            value={selectedLocation}
            onChange={handleLocationChange}
            isDisabled={!filters.associateCompany}
            className="select-control"
            components={{ DropdownIndicator: PerformerDropdownIndicator }}
          />
        </div>

        {showYearMonth && (
          <div className="filter-subcontainer">
            <label className="filter-label">
              <small>Year</small>
            </label>
            <Select
              options={yearOptions}
              value={selectedYear}
              onChange={handleYearChange}
              className="select-control"
              components={{ DropdownIndicator: PerformerDropdownIndicator }}
            />
          </div>
        )}

        {showYearMonth && (
          <div className="filter-subcontainer">
            <label className="filter-label">
              <small>Month</small>
            </label>
            <Select
              options={monthOptions}
              value={selectedMonth}
              onChange={handleMonthChange}
              isDisabled={!filters.year}
              className="select-control"
              components={{ DropdownIndicator: PerformerDropdownIndicator }}
            />
          </div>
        )}

        {showAdditionalFilters && filters.establishmentType === "CLRA" && (
          <div>
            <div className="d-flex flex-column">
              <button
                className="button-primary"
                onClick={onAdditionalFiltersClick}
              >
                More Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeUploadFilters;
